import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: API_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add request interceptor to include auth token
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Add response interceptor for error handling
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // Generic request method
  async request<T>(config: AxiosRequestConfig): Promise<T> {
    const response = await this.api.request(config);
    return response.data;
  }

  // Auth
  async login(email: string, password: string) {
    return this.request({
      method: 'POST',
      url: '/auth/login',
      data: { email, password },
    });
  }

  async getMe() {
    return this.request({ method: 'GET', url: '/auth/me' });
  }

  async changePassword(currentPassword: string, newPassword: string) {
    return this.request({
      method: 'POST',
      url: '/auth/change-password',
      data: { currentPassword, newPassword },
    });
  }

  // Employees
  async getEmployees(params?: any) {
    return this.request({ method: 'GET', url: '/employees', params });
  }

  async getEmployee(id: string) {
    return this.request({ method: 'GET', url: `/employees/${id}` });
  }

  async createEmployee(data: any) {
    return this.request({ method: 'POST', url: '/employees', data });
  }

  async updateEmployee(id: string, data: any) {
    return this.request({ method: 'PUT', url: `/employees/${id}`, data });
  }

  async deactivateEmployee(id: string) {
    return this.request({ method: 'PATCH', url: `/employees/${id}/deactivate` });
  }

  async deleteEmployee(id: string) {
    return this.request({ method: 'DELETE', url: `/employees/${id}` });
  }

  async resetEmployeePassword(id: string, newPassword: string) {
    return this.request({ method: 'POST', url: `/employees/${id}/reset-password`, data: { newPassword } });
  }

  async bulkSetCompensation(label: string, amount: number) {
    return this.request({ method: 'POST', url: '/employees/bulk-compensation', data: { label, amount } });
  }

  async upsertEmployeeCompensation(employeeId: string, label: string, amount: number) {
    return this.request({ method: 'POST', url: `/employees/${employeeId}/compensations`, data: { label, amount } });
  }

  async deleteEmployeeCompensation(employeeId: string, compensationId: string) {
    return this.request({ method: 'DELETE', url: `/employees/${employeeId}/compensations/${compensationId}` });
  }

  async getEmployeeTransfers(employeeId: string) {
    return this.request({ method: 'GET', url: `/employees/${employeeId}/transfers` });
  }

  async createEmployeeTransfer(employeeId: string, data: { accountType: string; accountIdentifier: string; amount: number }) {
    return this.request({ method: 'POST', url: `/employees/${employeeId}/transfers`, data });
  }

  async updateEmployeeTransfer(employeeId: string, transferId: string, data: { accountIdentifier: string; amount: number }) {
    return this.request({ method: 'PUT', url: `/employees/${employeeId}/transfers/${transferId}`, data });
  }

  async deleteEmployeeTransfer(employeeId: string, transferId: string) {
    return this.request({ method: 'DELETE', url: `/employees/${employeeId}/transfers/${transferId}` });
  }

  async getEmployeeStats() {
    return this.request({ method: 'GET', url: '/employees/stats' });
  }

  async updateEmailNotifications(emailNotifications: boolean) {
    return this.request({ method: 'PUT', url: '/auth/email-notifications', data: { emailNotifications } });
  }

  // Leaves
  async getLeaves(params?: any) {
    return this.request({ method: 'GET', url: '/leaves', params });
  }

  async getLeave(id: string) {
    return this.request({ method: 'GET', url: `/leaves/${id}` });
  }

  async applyLeave(data: any) {
    return this.request({ method: 'POST', url: '/leaves/apply', data });
  }

  async addUrgentLeave(data: any) {
    return this.request({ method: 'POST', url: '/leaves/urgent', data });
  }

  async approveLeave(id: string) {
    return this.request({ method: 'PUT', url: `/leaves/${id}/approve` });
  }

  async rejectLeave(id: string, rejectionReason: string) {
    return this.request({
      method: 'PUT',
      url: `/leaves/${id}/reject`,
      data: { rejectionReason },
    });
  }

  async cancelLeave(id: string) {
    return this.request({ method: 'DELETE', url: `/leaves/${id}` });
  }

  // Attendance
  async getAttendance(params?: any) {
    return this.request({ method: 'GET', url: '/attendance', params });
  }

  async getAttendanceSummary(employeeId: string, month: number, year: number) {
    return this.request({
      method: 'GET',
      url: `/attendance/summary/${employeeId}`,
      params: { month, year },
    });
  }

  async markAbsence(data: any) {
    return this.request({ method: 'POST', url: '/attendance/absence', data });
  }

  // Payroll
  async getPayrolls(params?: any) {
    return this.request({ method: 'GET', url: '/payroll', params });
  }

  async getPayroll(id: string) {
    return this.request({ method: 'GET', url: `/payroll/${id}` });
  }

  async processPayroll(month: number, year: number) {
    return this.request({
      method: 'POST',
      url: '/payroll/process',
      data: { month, year },
    });
  }

  async approvePayroll(id: string) {
    return this.request({ method: 'PUT', url: `/payroll/${id}/approve` });
  }

  async rejectPayroll(id: string, rejectionReason: string) {
    return this.request({ method: 'PUT', url: `/payroll/${id}/reject`, data: { rejectionReason } });
  }

  async reprocessPayroll(id: string) {
    return this.request({ method: 'POST', url: `/payroll/${id}/reprocess` });
  }

  async lockPayroll(id: string) {
    return this.request({ method: 'PUT', url: `/payroll/${id}/lock` });
  }

  async updatePayroll(id: string, data: any) {
    return this.request({ method: 'PUT', url: `/payroll/${id}`, data });
  }

  async deletePayroll(id: string) {
    return this.request({ method: 'DELETE', url: `/payroll/${id}` });
  }

  async resetPayroll(month: number, year: number) {
    return this.request({ method: 'POST', url: '/payroll/reset', data: { month, year } });
  }

  async submitPayroll(id: string) {
    return this.request({ method: 'PUT', url: `/payroll/${id}/submit` });
  }

  async submitAllPayrolls(month: number, year: number) {
    return this.request({ method: 'POST', url: '/payroll/submit-all', data: { month, year } });
  }

  // Payslips
  async generatePayslip(payrollId: string) {
    return this.request({
      method: 'POST',
      url: `/payslips/generate/${payrollId}`,
    });
  }

  async downloadPayslip(payrollId: string) {
    const response = await this.api.get(`/payslips/download/${payrollId}`, {
      responseType: 'blob',
    });
    const disposition = response.headers['content-disposition'] || '';
    const match = disposition.match(/filename="?([^"]+)"?/);
    const filename = match ? match[1] : `Payslip_${payrollId}.pdf`;
    const url = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  async getEmployeePayslips(employeeId: string) {
    return this.request({
      method: 'GET',
      url: `/payslips/employee/${employeeId}`,
    });
  }

  // Public Holidays
  async getHolidays(params?: any) {
    return this.request({ method: 'GET', url: '/holidays', params });
  }

  async getHoliday(id: string) {
    return this.request({ method: 'GET', url: `/holidays/${id}` });
  }

  async createHoliday(data: any) {
    return this.request({ method: 'POST', url: '/holidays', data });
  }

  async updateHoliday(id: string, data: any) {
    return this.request({ method: 'PUT', url: `/holidays/${id}`, data });
  }

  async deleteHoliday(id: string) {
    return this.request({ method: 'DELETE', url: `/holidays/${id}` });
  }

  async getUpcomingHolidays(limit?: number) {
    return this.request({
      method: 'GET',
      url: '/holidays/upcoming',
      params: { limit },
    });
  }

  async uploadHolidays(file: File) {
    const formData = new FormData();
    formData.append('file', file);
    return this.request({
      method: 'POST',
      url: '/holidays/upload',
      data: formData,
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  // System Config
  async getConfig() {
    return this.request({ method: 'GET', url: '/config' });
  }

  async getLeaveDefaults() {
    return this.request({ method: 'GET', url: '/config/leave-defaults' });
  }

  async getPayrollCycle(month: number, year: number) {
    return this.request({ method: 'GET', url: '/config/payroll-cycle', params: { month, year } });
  }

  async updateConfig(configs: Array<{ key: string; value: string; description?: string }>) {
    return this.request({ method: 'POST', url: '/config/batch', data: configs });
  }

  async getLeaveYearStatus() {
    return this.request({ method: 'GET', url: '/config/leave-year-status' });
  }

  async resetLeaveYear() {
    return this.request({ method: 'POST', url: '/config/reset-leave-year' });
  }

  // Reports
  async getDashboardStats() {
    return this.request({ method: 'GET', url: '/reports/dashboard' });
  }

  async getLeaveReport(params?: any) {
    return this.request({ method: 'GET', url: '/reports/leave', params });
  }

  async getLeaveBalancesReport(params?: any) {
    return this.request({ method: 'GET', url: '/reports/leave-balances', params });
  }

  async getAttendanceReport(params: any) {
    return this.request({ method: 'GET', url: '/reports/attendance', params });
  }

  async getPayrollReport(params?: any) {
    return this.request({ method: 'GET', url: '/reports/payroll', params });
  }
}

export default new ApiService();
